import React, { useRef, useEffect } from 'react';
import { AudioResult, SessionDataResult } from "interfaces/sessions";
import { Controls } from './Controls';
import { AudioPlayerBar } from './Timeline';
import { AudioPlayer } from './AudioPlayer';
import { prospectPartyCode } from '../../../../cfg/endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { startLoading, loadSuccess, loadError, clearDownloadState, toReduxSafeAudioDataResult, fromReduxSafeAudioDataResult } from '../../../../lib/redux/sessions/audio/audio-slice';
import { getServicesManager } from '../../../../services';
import { Typography } from '../../../../interfaces/typography';

export interface TranscriptAudioProps {
  data: SessionDataResult;  
  currentTimeInMs: number;
  isPlaying: boolean;
  previousPlayState: boolean;
  updateTime: (time?: number, isActive?: boolean, prevActive?: boolean) => void;
  setSpeed: (idx: number) => void;
  speed: number;
  totalDuration: number;
}

export const TranscriptAudio: React.FC<TranscriptAudioProps> = ({
  currentTimeInMs,
  isPlaying,
  previousPlayState,
  data,
  updateTime,
  setSpeed,
  speed,
  totalDuration
}) => {
  const dispatch = useDispatch();
  const audioPlayerRef = useRef<{ downloadAudio: () => void }>(null);
  
  const sessionId = data.session.session_id;
  
  // Get audio data from redux store
  const audioState = useSelector((state: RootState) => state.sessionAudio.sessionIdToAudio[sessionId]);
  const audioDataReduxSafe = audioState?.data;
  const audioData = audioDataReduxSafe ? fromReduxSafeAudioDataResult(audioDataReduxSafe) : null;
  const isLoading = audioState?.isLoading || false;
  const error = audioState?.error;
  
  // Get download state from redux store
  const isDownloading = audioState?.isDownloading || false;
  const downloadError = audioState?.downloadError;
  const downloadSuccess = audioState?.downloadSuccess || false;

  // Clear download success state after 3 seconds
  useEffect(() => {
    if (downloadSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearDownloadState(sessionId));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadSuccess, sessionId, dispatch]);

  // Simple effect to fetch audio if not available
  useEffect(() => {
    // If we already have data or are loading, do nothing
    if (audioData || isLoading || error) return;
    
    // Fetch audio data
    const fetchAudio = async () => {
      dispatch(startLoading(sessionId));
      try {
        const data = await getServicesManager().getAudioData(sessionId);
        if (data?.audio && data.audio_links) {
          dispatch(loadSuccess({ sessionId, data: toReduxSafeAudioDataResult(data) }));
        } else {
          dispatch(loadError({ sessionId, error: 'No audio data available' }));
        }
      } catch (err) {
        dispatch(loadError({ 
          sessionId, 
          error: err instanceof Error ? err.message : 'Failed to load audio' 
        }));
      }
    };

    fetchAudio();
  }, [sessionId, audioData, isLoading, error, dispatch]);

  // Show error state
  if (error) {
    return (
      <div className="min-h-fit w-full bg-white border-t border-gray-200 flex items-center justify-center py-8">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 text-red-500">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <Typography variant="smallParagraph" className="text-red-500">
            {error}
          </Typography>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading || !audioData) {
    return (
      <div className="min-h-fit w-full bg-white border-t border-gray-200 flex items-center justify-center py-8">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
          <Typography variant="smallParagraph" className="text-gray-600">
            Loading audio player...
          </Typography>
        </div>
      </div>
    );
  }

  // Handle play/pause toggle
  const handlePlayPause = () => updateTime(undefined, !isPlaying, isPlaying);
  const handleTimelineUpdate = (percentage: number) => {
    if (totalDuration) {
      const newTime = percentage * totalDuration;
      updateTime(newTime, undefined);
    }
  }

  // Handle mouse down on timeline - pause playback while scrubbing
  const handleMouseDown = () => {
    if (isPlaying) { updateTime(undefined, false, true) }
  }

  // Handle mouse up on timeline - resume playback if we were playing before
  const handleMouseUp = () => {
    if (previousPlayState) { updateTime(undefined, true, false) }
  }

  // Handle download audio
  const handleDownload = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.downloadAudio();
    }
  }

  const prospectNotRecorded = audioData.audio ? 
    audioData.audio.find((value: AudioResult) => value.party_code === prospectPartyCode && value.audio_status === 'UPLOADED') === undefined : 
    true

  return (
    <div className="min-h-fit w-full bg-white border-t border-gray-200 flex flex-row gap-2 overflow-hidden pt-2 pl-2 pr-2">
      <Controls 
        isPlaying={isPlaying} 
        currentSpeed={speed} 
        currentTimeInMS={currentTimeInMs} 
        endTime={totalDuration}
        onPlayPause={handlePlayPause} 
        onSpeedChange={setSpeed}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        downloadError={downloadError}
        downloadSuccess={downloadSuccess}
      />
      <AudioPlayerBar
        currentTime={currentTimeInMs}
        updateCurrentTime={handleTimelineUpdate}
        endTime={totalDuration}
        turns={data?.turns || []}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        play={isPlaying}
        prospectNotRecorded={prospectNotRecorded}
      />
      
      {/* Audio player component that handles actual audio playback */}
      <AudioPlayer
        ref={audioPlayerRef}
        sessionId={sessionId}
        currentTimeInMS={currentTimeInMs}
        isPlaying={isPlaying}
        speed={speed}
        data={audioData}
      />
    </div>
  );
}
